import React, { useState, useEffect } from 'react';
import axios from 'axios';

const TABLES = [
  'users', 'categories', 'family_activities', 'family_events', 'family_goals',
  'family_members', 'goal_steps', 'goals', 'investment_assets', 'investment_transactions',
  'investments', 'life_categories', 'projects', 'relationship_status', 'tasks',
  'transactions', 'ai_plans', 'ai_chat_sessions', 'ai_chat_messages'
];

const API_BASE_URL = process.env.REACT_APP_API_URL || 
  (process.env.NODE_ENV === 'production' ? '' : 'http://localhost:5001');

interface AdminStats {
  users: {
    total: number;
    active: number;
  };
  goals: number;
  tasks: {
    total: number;
    byStatus: Record<string, number>;
  };
}

interface User {
  id: number;
  name: string;
  email: string;
  role: 'user' | 'admin';
  createdAt: string;
  updatedAt: string;
  stats: {
    goals: number;
    tasks: number;
  };
}

const AdminPanel: React.FC = () => {
  const [stats, setStats] = useState<AdminStats | null>(null);
  const [users, setUsers] = useState<User[]>([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState<'stats' | 'users' | 'tables'>('stats');
  const [selectedTable, setSelectedTable] = useState<string>('users');
  const [tableData, setTableData] = useState<any[]>([]);
  const [tableColumns, setTableColumns] = useState<string[]>([]);

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('token');
      const headers = { Authorization: `Bearer ${token}` };

      const [statsRes, usersRes] = await Promise.all([
        axios.get(`${API_BASE_URL}/api/admin/stats`, { headers }),
        axios.get(`${API_BASE_URL}/api/admin/users`, { headers })
      ]);

      setStats(statsRes.data);
      setUsers(usersRes.data);
    } catch (error: any) {
      console.error('Failed to load admin data:', error);
      if (error.response?.status === 403) {
        alert('У вас нет прав администратора');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleChangeRole = async (userId: number, newRole: 'user' | 'admin') => {
    if (!window.confirm(`Изменить роль пользователя на ${newRole}?`)) return;

    try {
      const token = localStorage.getItem('token');
      await axios.patch(
        `${API_BASE_URL}/api/admin/users/${userId}/role`,
        { role: newRole },
        { headers: { Authorization: `Bearer ${token}` } }
      );

      alert('Роль успешно изменена');
      loadData();
    } catch (error) {
      console.error('Failed to change role:', error);
      alert('Ошибка изменения роли');
    }
  };

  const handleDeleteUser = async (userId: number, userName: string) => {
    if (!window.confirm(`Удалить пользователя ${userName}? Все его данные будут удалены!`)) return;

    try {
      const token = localStorage.getItem('token');
      await axios.delete(
        `${API_BASE_URL}/api/admin/users/${userId}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );

      alert('Пользователь удален');
      loadData();
    } catch (error) {
      console.error('Failed to delete user:', error);
      alert('Ошибка удаления пользователя');
    }
  };

  const loadTableData = async (tableName: string) => {
    try {
      setLoading(true);
      const token = localStorage.getItem('token');
      const response = await axios.get(
        `${API_BASE_URL}/api/admin/tables/${tableName}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      setTableData(response.data.data);
      setTableColumns(response.data.columns);
    } catch (error) {
      console.error('Failed to load table data:', error);
      alert('Ошибка загрузки данных таблицы');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (activeTab === 'tables') {
      loadTableData(selectedTable);
    }
  }, [selectedTable, activeTab]);

  if (loading && activeTab !== 'tables') {
    return <div style={{ padding: '20px', textAlign: 'center' }}>Загрузка...</div>;
  }

  return (
    <div style={{ padding: '20px', maxWidth: '1400px', margin: '0 auto' }}>
      <h1 style={{ marginBottom: '30px' }}>👑 Панель администратора</h1>

      {/* Tabs */}
      <div style={{ display: 'flex', gap: '10px', marginBottom: '20px', borderBottom: '2px solid #e5e7eb' }}>
        <button
          onClick={() => setActiveTab('stats')}
          style={{
            padding: '10px 20px',
            border: 'none',
            background: activeTab === 'stats' ? '#3b82f6' : 'transparent',
            color: activeTab === 'stats' ? 'white' : '#6b7280',
            cursor: 'pointer',
            borderRadius: '8px 8px 0 0',
            fontWeight: activeTab === 'stats' ? 'bold' : 'normal'
          }}
        >
          📊 Статистика
        </button>
        <button
          onClick={() => setActiveTab('tables')}
          style={{
            padding: '10px 20px',
            border: 'none',
            background: activeTab === 'tables' ? '#3b82f6' : 'transparent',
            color: activeTab === 'tables' ? 'white' : '#6b7280',
            cursor: 'pointer',
            borderRadius: '8px 8px 0 0',
            fontWeight: activeTab === 'tables' ? 'bold' : 'normal'
          }}
        >
          🗄️ Таблицы
        </button>
        <button
          onClick={() => setActiveTab('users')}
          style={{
            padding: '10px 20px',
            border: 'none',
            background: activeTab === 'users' ? '#3b82f6' : 'transparent',
            color: activeTab === 'users' ? 'white' : '#6b7280',
            cursor: 'pointer',
            borderRadius: '8px 8px 0 0',
            fontWeight: activeTab === 'users' ? 'bold' : 'normal'
          }}
        >
          👥 Пользователи
        </button>
      </div>

      {/* Stats Tab */}
      {activeTab === 'stats' && stats && (
        <div>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))', gap: '20px', marginBottom: '30px' }}>
            <div style={{ background: '#f3f4f6', padding: '20px', borderRadius: '8px' }}>
              <h3 style={{ margin: '0 0 10px 0', color: '#6b7280' }}>Пользователи</h3>
              <p style={{ fontSize: '32px', fontWeight: 'bold', margin: '0' }}>{stats.users.total}</p>
              <p style={{ fontSize: '14px', color: '#6b7280', margin: '5px 0 0 0' }}>
                {stats.users.active} активных (30 дней)
              </p>
            </div>

            <div style={{ background: '#dbeafe', padding: '20px', borderRadius: '8px' }}>
              <h3 style={{ margin: '0 0 10px 0', color: '#1e40af' }}>Цели</h3>
              <p style={{ fontSize: '32px', fontWeight: 'bold', margin: '0', color: '#1e40af' }}>{stats.goals}</p>
            </div>

            <div style={{ background: '#fef3c7', padding: '20px', borderRadius: '8px' }}>
              <h3 style={{ margin: '0 0 10px 0', color: '#92400e' }}>Задачи</h3>
              <p style={{ fontSize: '32px', fontWeight: 'bold', margin: '0', color: '#92400e' }}>{stats.tasks.total}</p>
            </div>
          </div>

          <div style={{ background: 'white', padding: '20px', borderRadius: '8px', border: '1px solid #e5e7eb' }}>
            <h3 style={{ marginTop: 0 }}>Задачи по статусам</h3>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(150px, 1fr))', gap: '10px' }}>
              {Object.entries(stats.tasks.byStatus).map(([status, count]) => (
                <div key={status} style={{ padding: '10px', background: '#f9fafb', borderRadius: '6px', textAlign: 'center' }}>
                  <div style={{ fontSize: '24px', fontWeight: 'bold' }}>{count}</div>
                  <div style={{ fontSize: '12px', color: '#6b7280' }}>{status}</div>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}

      {/* Tables Tab */}
      {activeTab === 'tables' && (
        <div>
          {/* Table selector */}
          <div style={{ marginBottom: '20px', background: 'white', padding: '15px', borderRadius: '8px', border: '1px solid #e5e7eb' }}>
            <label style={{ display: 'block', marginBottom: '10px', fontWeight: 'bold' }}>Выберите таблицу:</label>
            <select
              value={selectedTable}
              onChange={(e) => setSelectedTable(e.target.value)}
              style={{
                width: '100%',
                padding: '10px',
                borderRadius: '6px',
                border: '1px solid #d1d5db',
                fontSize: '14px'
              }}
            >
              {TABLES.map(table => (
                <option key={table} value={table}>{table}</option>
              ))}
            </select>
          </div>

          {/* Table data */}
          {loading ? (
            <div style={{ textAlign: 'center', padding: '40px' }}>Загрузка данных...</div>
          ) : tableData.length === 0 ? (
            <div style={{ textAlign: 'center', padding: '40px', background: '#f9fafb', borderRadius: '8px' }}>
              Таблица пуста
            </div>
          ) : (
            <div style={{ overflowX: 'auto', background: 'white', borderRadius: '8px', border: '1px solid #e5e7eb' }}>
              <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                <thead>
                  <tr style={{ background: '#f3f4f6' }}>
                    {tableColumns.map(column => (
                      <th key={column} style={{ padding: '12px', textAlign: 'left', borderBottom: '2px solid #e5e7eb', whiteSpace: 'nowrap' }}>
                        {column}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {tableData.map((row, index) => (
                    <tr key={index} style={{ borderBottom: '1px solid #e5e7eb' }}>
                      {tableColumns.map(column => (
                        <td key={column} style={{ padding: '12px', maxWidth: '300px', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                          {typeof row[column] === 'object' ? JSON.stringify(row[column]) : String(row[column] ?? '')}
                        </td>
                      ))}
                    </tr>
                  ))}
                </tbody>
              </table>
              <div style={{ padding: '15px', background: '#f9fafb', textAlign: 'center', color: '#6b7280', fontSize: '14px' }}>
                Всего записей: {tableData.length}
              </div>
            </div>
          )}
        </div>
      )}

      {/* Users Tab */}
      {activeTab === 'users' && (
        <div>
          <table style={{ width: '100%', borderCollapse: 'collapse', background: 'white', borderRadius: '8px', overflow: 'hidden' }}>
            <thead>
              <tr style={{ background: '#f3f4f6' }}>
                <th style={{ padding: '12px', textAlign: 'left', borderBottom: '2px solid #e5e7eb' }}>ID</th>
                <th style={{ padding: '12px', textAlign: 'left', borderBottom: '2px solid #e5e7eb' }}>Имя</th>
                <th style={{ padding: '12px', textAlign: 'left', borderBottom: '2px solid #e5e7eb' }}>Email</th>
                <th style={{ padding: '12px', textAlign: 'left', borderBottom: '2px solid #e5e7eb' }}>Роль</th>
                <th style={{ padding: '12px', textAlign: 'left', borderBottom: '2px solid #e5e7eb' }}>Цели/Задачи</th>
                <th style={{ padding: '12px', textAlign: 'left', borderBottom: '2px solid #e5e7eb' }}>Дата регистрации</th>
                <th style={{ padding: '12px', textAlign: 'left', borderBottom: '2px solid #e5e7eb' }}>Действия</th>
              </tr>
            </thead>
            <tbody>
              {users.map((user) => (
                <tr key={user.id} style={{ borderBottom: '1px solid #e5e7eb' }}>
                  <td style={{ padding: '12px' }}>{user.id}</td>
                  <td style={{ padding: '12px' }}>{user.name}</td>
                  <td style={{ padding: '12px' }}>{user.email}</td>
                  <td style={{ padding: '12px' }}>
                    <span style={{
                      padding: '4px 8px',
                      borderRadius: '4px',
                      fontSize: '12px',
                      fontWeight: 'bold',
                      background: user.role === 'admin' ? '#fee2e2' : '#dbeafe',
                      color: user.role === 'admin' ? '#991b1b' : '#1e40af'
                    }}>
                      {user.role === 'admin' ? '👑 Админ' : '👤 Юзер'}
                    </span>
                  </td>
                  <td style={{ padding: '12px' }}>{user.stats.goals} / {user.stats.tasks}</td>
                  <td style={{ padding: '12px', fontSize: '14px', color: '#6b7280' }}>
                    {new Date(user.createdAt).toLocaleDateString('ru-RU')}
                  </td>
                  <td style={{ padding: '12px' }}>
                    <div style={{ display: 'flex', gap: '5px' }}>
                      <button
                        onClick={() => handleChangeRole(user.id, user.role === 'admin' ? 'user' : 'admin')}
                        style={{
                          padding: '4px 8px',
                          fontSize: '12px',
                          background: '#3b82f6',
                          color: 'white',
                          border: 'none',
                          borderRadius: '4px',
                          cursor: 'pointer'
                        }}
                      >
                        {user.role === 'admin' ? '↓ User' : '↑ Admin'}
                      </button>
                      <button
                        onClick={() => handleDeleteUser(user.id, user.name)}
                        style={{
                          padding: '4px 8px',
                          fontSize: '12px',
                          background: '#ef4444',
                          color: 'white',
                          border: 'none',
                          borderRadius: '4px',
                          cursor: 'pointer'
                        }}
                      >
                        🗑️ Удалить
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};

export default AdminPanel;
